/**
 * Pure text transforms behind the RichTextEditor toolbar / slash menu / paste
 * cleanup. Kept framework-free so the wrap/unwrap and HTML→markdown logic is
 * unit-testable without a DOM-mounted component.
 */

export type Selection = { value: string; start: number; end: number };
export type EditResult = { value: string; start: number; end: number };

/**
 * Toggle an inline marker (`**`, `*`, `` ` ``, `~~`) around the selection.
 * If the selection is already wrapped, unwrap it; otherwise wrap it. With an
 * empty selection, inserts the markers and places the caret between them.
 */
export function applyInlineFormat(sel: Selection, marker: string): EditResult {
  const { value, start, end } = sel;
  const selected = value.slice(start, end);
  const before = value.slice(0, start);
  const after = value.slice(end);

  if (
    selected.length >= marker.length * 2 &&
    selected.startsWith(marker) &&
    selected.endsWith(marker)
  ) {
    const inner = selected.slice(
      marker.length,
      selected.length - marker.length,
    );
    return { value: before + inner + after, start, end: start + inner.length };
  }
  // Already wrapped *outside* the selection? Unwrap those.
  if (before.endsWith(marker) && after.startsWith(marker)) {
    return {
      value:
        before.slice(0, before.length - marker.length) +
        selected +
        after.slice(marker.length),
      start: start - marker.length,
      end: end - marker.length,
    };
  }
  const wrapped = marker + selected + marker;
  return {
    value: before + wrapped + after,
    start: start + marker.length,
    end: end + marker.length,
  };
}

/**
 * Toggle a line prefix (`# `, `## `, `- `, `> `, `1. `) on every line the
 * selection touches. If every line already has the prefix, strip it.
 * `ordered` renumbers `1.`, `2.`, … instead of a static prefix.
 */
export function toggleLinePrefix(
  sel: Selection,
  prefix: string,
  ordered = false,
): EditResult {
  const { value, start, end } = sel;
  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  let lineEnd = value.indexOf("\n", end);
  if (lineEnd === -1) lineEnd = value.length;

  const block = value.slice(lineStart, lineEnd);
  const lines = block.split("\n");
  const ordRe = /^\d+\.\s/;
  const allHave = ordered
    ? lines.every((l) => ordRe.test(l))
    : lines.every((l) => l.startsWith(prefix));

  const next = lines
    .map((l, i) => {
      if (ordered) {
        return allHave ? l.replace(ordRe, "") : `${i + 1}. ${l}`;
      }
      return allHave ? l.slice(prefix.length) : prefix + l;
    })
    .join("\n");

  const newValue = value.slice(0, lineStart) + next + value.slice(lineEnd);
  return { value: newValue, start: lineStart, end: lineStart + next.length };
}

/** Wrap the selection as a markdown link `[text](url)`. */
export function insertLink(sel: Selection, url: string): EditResult {
  const { value, start, end } = sel;
  const text = value.slice(start, end) || "link";
  const snippet = `[${text}](${url})`;
  return {
    value: value.slice(0, start) + snippet + value.slice(end),
    start: start + snippet.length,
    end: start + snippet.length,
  };
}

/** Replace the selection with `text`, placing the caret at its end. */
export function insertText(sel: Selection, text: string): EditResult {
  const { value, start, end } = sel;
  return {
    value: value.slice(0, start) + text + value.slice(end),
    start: start + text.length,
    end: start + text.length,
  };
}

/** Insert a fenced code block, caret inside. */
export function insertCodeBlock(sel: Selection): EditResult {
  const { value, start } = sel;
  const atLineStart = start === 0 || value[start - 1] === "\n";
  const lead = atLineStart ? "" : "\n";
  const head = `${lead}\`\`\`\n`;
  const tail = "\n```\n";
  const caret = start + head.length;
  return {
    value: value.slice(0, start) + head + tail + value.slice(start),
    start: caret,
    end: caret,
  };
}

const INLINE_TAGS: Record<string, [string, string]> = {
  STRONG: ["**", "**"],
  B: ["**", "**"],
  EM: ["*", "*"],
  I: ["*", "*"],
  CODE: ["`", "`"],
  DEL: ["~~", "~~"],
  S: ["~~", "~~"],
};

/**
 * Minimal HTML→markdown for paste cleanup. Converts the supported formatting
 * subset (headings, bold/italic/code/strike, links, lists, blockquote, code
 * blocks, paragraphs/line breaks) and drops everything else to its text — so a
 * paste from a web page or doc lands as clean markdown instead of raw HTML.
 */
export function htmlToMarkdown(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");

  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent || "").replace(/\s+/g, " ");
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return "";
    const el = node as HTMLElement;
    const tag = el.tagName;
    const inner = Array.from(el.childNodes).map(walk).join("");

    if (tag in INLINE_TAGS) {
      const [open, close] = INLINE_TAGS[tag]!;
      return inner.trim() ? `${open}${inner.trim()}${close}` : inner;
    }
    if (/^H[1-6]$/.test(tag)) {
      const level = Number(tag[1]);
      return `\n\n${"#".repeat(level)} ${inner.trim()}\n\n`;
    }
    if (tag === "A") {
      const href = el.getAttribute("href") || "";
      return href ? `[${inner.trim() || href}](${href})` : inner;
    }
    if (tag === "BR") return "\n";
    if (tag === "P" || tag === "DIV") return `\n\n${inner.trim()}\n\n`;
    if (tag === "BLOCKQUOTE") {
      return `\n\n${inner
        .trim()
        .split("\n")
        .map((l) => `> ${l}`)
        .join("\n")}\n\n`;
    }
    if (tag === "PRE") return `\n\n\`\`\`\n${el.textContent || ""}\n\`\`\`\n\n`;
    if (tag === "LI") {
      const ordered = el.parentElement?.tagName === "OL";
      return `${ordered ? "1." : "-"} ${inner.trim()}\n`;
    }
    if (tag === "UL" || tag === "OL") return `\n${inner}\n`;
    return inner;
  };

  return walk(doc.body)
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]+\n/g, "\n")
    .trim();
}
